import { useState, useEffect } from 'react'
import { supabase } from '../../services/supabase'
import { Modal } from '../Common/Modal'
import { Button } from '../Common/Button'

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'advisor_codes', label: 'Advisor Codes' },
]

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-sfp-orange' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  )
}

export function ProductProviderForm({ initialData, onSave, onClose, sidebarCollapsed, isDarkMode = false }) {
  const [activeTab, setActiveTab] = useState('details')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedProviderId, setSavedProviderId] = useState(initialData?.id || null)

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    contact_count: initialData?.contact_count ?? 0,
    active: initialData?.active ?? true,
  })

  // Contacts state
  const [contacts, setContacts] = useState([])
  const [contactsLoading, setContactsLoading] = useState(false)

  // Advisor codes state
  const [advisorCodes, setAdvisorCodes] = useState([])
  const [advisors, setAdvisors] = useState([])

  // Load sub-data when we have a provider ID
  useEffect(() => {
    if (!savedProviderId) return

    const loadContacts = async () => {
      setContactsLoading(true)
      const { data } = await supabase
        .from('provider_contacts')
        .select('*')
        .eq('provider_id', savedProviderId)
        .order('created_at')
      setContacts(data || [])
      setContactsLoading(false)
    }

    const loadAdvisorCodes = async () => {
      const { data } = await supabase
        .from('provider_advisor_codes')
        .select('*, advisors(id, full_name)')
        .eq('provider_id', savedProviderId)
        .order('created_at')
      setAdvisorCodes(data || [])
    }

    loadContacts()
    loadAdvisorCodes()
  }, [savedProviderId])

  useEffect(() => {
    supabase
      .from('advisors')
      .select('id, full_name, advisor_code')
      .eq('active', true)
      .order('full_name')
      .then(({ data }) => setAdvisors(data || []))
  }, [])

  const handleChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : value }))
  }

  const handleSaveDetails = async () => {
    if (!formData.name.trim()) { setError('Name is required'); return }
    setError('')
    setSaving(true)
    try {
      const result = await onSave(formData, savedProviderId)
      if (result?.id) setSavedProviderId(result.id)
      setActiveTab('contacts')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // ── Contacts ──────────────────────────────────────────────
  const addContact = () => {
    setContacts(prev => [...prev, {
      _new: true,
      contact_name: '',
      contact_number: '',
      contact_email: '',
      description: '',
      active: true,
    }])
  }

  const updateContactField = (idx, field, value) => {
    setContacts(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c))
  }

  const saveContact = async (idx) => {
    const contact = contacts[idx]
    const payload = {
      provider_id: savedProviderId,
      contact_name: contact.contact_name,
      contact_number: contact.contact_number,
      contact_email: contact.contact_email,
      description: contact.description,
      active: contact.active,
    }
    if (contact._new || !contact.id) {
      const { data, error } = await supabase.from('provider_contacts').insert([payload]).select()
      if (error) { alert(error.message); return }
      setContacts(prev => prev.map((c, i) => i === idx ? data[0] : c))
    } else {
      const { error } = await supabase.from('provider_contacts').update(payload).eq('id', contact.id)
      if (error) { alert(error.message); return }
    }
  }

  const removeContact = async (idx) => {
    const contact = contacts[idx]
    if (contact.id && !contact._new) {
      await supabase.from('provider_contacts').delete().eq('id', contact.id)
    }
    setContacts(prev => prev.filter((_, i) => i !== idx))
  }

  // ── Advisor Codes ─────────────────────────────────────────
  const addAdvisorCode = () => {
    setAdvisorCodes(prev => [...prev, {
      _new: true,
      advisor_id: '',
      advisor_code: '',
      active: true,
    }])
  }

  const updateAdvisorCodeField = (idx, field, value) => {
    // Auto-fill advisor_code from selected advisor if available
    if (field === 'advisor_id') {
      const advisor = advisors.find(a => a.id === value)
      setAdvisorCodes(prev => prev.map((ac, i) => i === idx
        ? { ...ac, advisor_id: value, advisor_code: advisor?.advisor_code || ac.advisor_code }
        : ac
      ))
    } else {
      setAdvisorCodes(prev => prev.map((ac, i) => i === idx ? { ...ac, [field]: value } : ac))
    }
  }

  const saveAdvisorCode = async (idx) => {
    const ac = advisorCodes[idx]
    const payload = {
      provider_id: savedProviderId,
      advisor_id: ac.advisor_id || null,
      advisor_code: ac.advisor_code,
      active: ac.active,
    }
    if (ac._new || !ac.id) {
      const { data, error } = await supabase.from('provider_advisor_codes').insert([payload]).select()
      if (error) { alert(error.message); return }
      setAdvisorCodes(prev => prev.map((c, i) => i === idx ? { ...data[0], advisors: advisors.find(a => a.id === data[0].advisor_id) } : c))
    } else {
      const { error } = await supabase.from('provider_advisor_codes').update(payload).eq('id', ac.id)
      if (error) { alert(error.message); return }
    }
  }

  const removeAdvisorCode = async (idx) => {
    const ac = advisorCodes[idx]
    if (ac.id && !ac._new) {
      await supabase.from('provider_advisor_codes').delete().eq('id', ac.id)
    }
    setAdvisorCodes(prev => prev.filter((_, i) => i !== idx))
  }

  const notSavedYet = !savedProviderId

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={savedProviderId ? `Edit Provider: ${formData.name}` : 'Add Product Provider'}
      sidebarCollapsed={sidebarCollapsed}
      isDarkMode={isDarkMode}
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => !notSavedYet || tab.id === 'details' ? setActiveTab(tab.id) : null}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === tab.id
                ? 'border-sfp-orange text-sfp-orange'
                : notSavedYet && tab.id !== 'details'
                ? 'border-transparent text-gray-300 cursor-not-allowed'
                : 'border-transparent text-gray-500 hover:text-sfp-darkgray'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Details Tab ── */}
      {activeTab === 'details' && (
        <div className="space-y-4 max-w-lg">
          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Name *</label>
            <input name="name" value={formData.name} onChange={handleChange} className="input-field w-full" placeholder="e.g. Santam" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Contact Count</label>
            <input name="contact_count" type="number" min="0" value={formData.contact_count} onChange={handleChange} className="input-field w-full" />
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Active</span>
            <Toggle checked={formData.active} onChange={(val) => setFormData(p => ({ ...p, active: val }))} />
          </div>

          <div className="pt-2">
            <Button variant="primary" type="button" onClick={handleSaveDetails} disabled={saving}>
              {saving ? 'Saving...' : savedProviderId ? 'Update & Next' : 'Save & Continue'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Contacts Tab ── */}
      {activeTab === 'contacts' && (
        <div className="space-y-4">
          {notSavedYet ? (
            <p className="text-gray-400 text-sm">Save provider details first.</p>
          ) : (
            <>
              {contactsLoading ? (
                <p className="text-gray-400 text-sm">Loading...</p>
              ) : (
                <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
                  {contacts.map((contact, idx) => (
                    <div key={idx} className="p-4 border border-gray-200 rounded space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-sm" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Contact {idx + 1}</span>
                        <button type="button" onClick={() => removeContact(idx)} className="text-red-500 hover:text-red-700 text-xs">✕ Remove</button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Contact Name</label>
                          <input value={contact.contact_name || ''} onChange={(e) => updateContactField(idx, 'contact_name', e.target.value)} className="input-field w-full text-sm py-1" placeholder="Full name" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Contact Number</label>
                          <input value={contact.contact_number || ''} onChange={(e) => updateContactField(idx, 'contact_number', e.target.value)} className="input-field w-full text-sm py-1" placeholder="+27 82 000 0000" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Contact Email</label>
                          <input type="email" value={contact.contact_email || ''} onChange={(e) => updateContactField(idx, 'contact_email', e.target.value)} className="input-field w-full text-sm py-1" placeholder="contact@provider.co.za" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Description</label>
                          <input value={contact.description || ''} onChange={(e) => updateContactField(idx, 'description', e.target.value)} className="input-field w-full text-sm py-1" placeholder="e.g. Claims dept" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                          <span>Active</span>
                          <Toggle checked={contact.active ?? true} onChange={(val) => updateContactField(idx, 'active', val)} />
                        </label>
                        <Button variant="secondary" type="button" onClick={() => saveContact(idx)}>
                          Save Contact
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button variant="secondary" type="button" onClick={addContact} className="w-full">
                + Add Contact
              </Button>
            </>
          )}
        </div>
      )}

      {/* ── Advisor Codes Tab ── */}
      {activeTab === 'advisor_codes' && (
        <div className="space-y-4">
          {notSavedYet ? (
            <p className="text-gray-400 text-sm">Save provider details first.</p>
          ) : (
            <>
              <p className="text-xs text-gray-400">Link advisors and their codes for this provider.</p>
              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                {advisorCodes.map((ac, idx) => (
                  <div key={idx} className="p-4 border border-gray-200 rounded space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Entry {idx + 1}</span>
                      <button type="button" onClick={() => removeAdvisorCode(idx)} className="text-red-500 hover:text-red-700 text-xs">✕ Remove</button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Advisor</label>
                        <select
                          value={ac.advisor_id || ''}
                          onChange={(e) => updateAdvisorCodeField(idx, 'advisor_id', e.target.value)}
                          className="input-field w-full text-sm py-1"
                        >
                          <option value="">Select advisor...</option>
                          {advisors.map(a => (
                            <option key={a.id} value={a.id}>{a.full_name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Advisor Code</label>
                        <input
                          value={ac.advisor_code || ''}
                          onChange={(e) => updateAdvisorCodeField(idx, 'advisor_code', e.target.value)}
                          className="input-field w-full text-sm py-1"
                          placeholder="e.g. FSP12345"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                        <span>Active</span>
                        <Toggle checked={ac.active ?? true} onChange={(val) => updateAdvisorCodeField(idx, 'active', val)} />
                      </label>
                      <Button variant="secondary" type="button" onClick={() => saveAdvisorCode(idx)}>
                        Save Entry
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="secondary" type="button" onClick={addAdvisorCode} className="w-full">
                + Add Advisor Code
              </Button>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
